import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Flex, Modal, Table, Tag, Typography, notification } from "antd";
import { CheckCircleTwoTone, CloseCircleTwoTone, CloudServerOutlined, LoadingOutlined } from "@ant-design/icons";
import { styles } from "./FileUploadingConstants";
import {
	useCSVReader,
	lightenDarkenColor,
	formatFileSize,
} from "react-papaparse";
import { updateOrganization } from "../../../redux/organizationSlice";
import { createJob } from "../../../redux/jobsSlice";

const { Title } = Typography;
const DEFAULT_REMOVE_HOVER_COLOR = "#A01919";
const REMOVE_HOVER_COLOR_LIGHT = lightenDarkenColor(
	DEFAULT_REMOVE_HOVER_COLOR,
	40
);
const Default_Services=['repair','maintenance','estimate'];
const JobsMappingUpload = () => {
	const dispatch = useDispatch();
	const {
		organizationData,
		serviceTitanBusinessUnits,
		serviceTitanJobs,
		loading,
	} = useSelector((state) => state?.organization);
	const { trades } = useSelector((state) => state?.trade);
	const { jobs } = useSelector((state) => state?.jobs);

	const { CSVReader } = useCSVReader();
	const [zoneHover, setZoneHover] = useState(false);
	const [uploadedMappings, setUploadedMappings] = useState(null);
	const [removeHoverColor, setRemoveHoverColor] = useState(
		DEFAULT_REMOVE_HOVER_COLOR
	);
	const [missingJobs, setMissingJobs] = useState([]);
	const [missingJobsModalOpen, setMissingJobsModalOpen] = useState(false);
	const [selectedMissingJobKeys, setSelectedMissingJobKeys] = useState([]);
	const [creatingMissingJobs, setCreatingMissingJobs] = useState(false);
	const [jobCreationStatus, setJobCreationStatus] = useState({});

	const buildJobMapping = (row, jobId, jobItemName, serviceType) => {
		const {
			default_dispatch_header,
			default_dispatch_body,
			default_dispatch_text,
		} = organizationData;
		return {
			jobItem: jobId,
			jobItemName,
			serviceType,
			is_active: row[3]?.toLowerCase() === "yes" ? true : false,
			st_business_unit_id:
				serviceTitanBusinessUnits?.find((e) => e?.name === row[4])?.id || "",
			st_business_unit_name:
				serviceTitanBusinessUnits?.find((e) => e?.name === row[4])?.name || "",
			st_non_member_job_id:
				serviceTitanJobs?.find((e) => e?.name === row[5])?.id || "",
			st_non_member_job_name:
				serviceTitanJobs?.find((e) => e?.name === row[5])?.name || "",
			is_member: true,
			st_member_job_id:
				serviceTitanJobs?.find((e) => e?.name === row[6])?.id || "",
			st_member_job_name:
				serviceTitanJobs?.find((e) => e?.name === row[6])?.name || "",
			dispatch_fee: row[7]?.replace("$", "") || 0,
			member_dispatch_fee: row[8]?.replace("$", "") || 0,
			use_default_dispatch:
				default_dispatch_header.trim() === row[9].trim() &&
				default_dispatch_body.trim() === row[10].trim() &&
				default_dispatch_text.trim() === row[11].trim()
					? true
					: false,
			dispatch_header: row[9],
			dispatch_body: row[10],
			dispatch_text: row[11],
			availability_mode: row[12]?.toLowerCase().includes("office")
				? "office_hours"
				: "24/7",
		};
	};

	const insertMappingForTrade = (mappingsStructure, tradeId, serviceType, jobMapping) => {
		let tradeIndex = mappingsStructure.findIndex((e) => e.trade === tradeId);
		if (tradeIndex === -1) {
			mappingsStructure.push({
				trade: tradeId,
				repair: [],
				maintenance: [],
				estimate: [],
				additionalServices: {},
			});
			tradeIndex = mappingsStructure.length - 1;
		}
		if (Default_Services.includes(serviceType)) {
			mappingsStructure[tradeIndex][serviceType].push(jobMapping);
		} else {
			if (!mappingsStructure[tradeIndex].additionalServices[serviceType]) {
				mappingsStructure[tradeIndex].additionalServices[serviceType] = [];
			}
			mappingsStructure[tradeIndex].additionalServices[serviceType].push(jobMapping);
		}
	};

	const handleMappingsUpload = (results) => {
		let mappingsStructure = [];
		const uniqueTradesInCsv = new Set();
		results?.data?.forEach((row, index) => {
			if (index > 0) {
				const trade = trades?.find((e) => e?.tradeName === row[0]);
				if (trade) {
					uniqueTradesInCsv.add(trade._id);
				}
			}
		});
		mappingsStructure = Array.from(uniqueTradesInCsv).map(tradeId => ({
			trade: tradeId,
			repair: [],
			maintenance: [],
			estimate: [],
			additionalServices: {}
		}));

		const missing = [];
		const missingSeen = new Set();

		results?.data?.forEach((row, index) => {
			if (index > 0) {
				const trade = trades?.find((e) => e?.tradeName === row[0]);
				const serviceType = row[2]?.toLowerCase();

				if (!trade || !serviceType) return;

				const job = jobs?.find(
					(e) =>
						e?.jobItem === row[1] &&
						e?.serviceType === serviceType &&
						e?.trade?._id === trade?._id
				);

				if (job) {
					const jobMapping = buildJobMapping(row, job._id, job.jobItem, serviceType);
					insertMappingForTrade(mappingsStructure, trade._id, serviceType, jobMapping);
				} else {
					const key = `${trade._id}|${row[1]}|${serviceType}`;
					if (!missingSeen.has(key)) {
						missingSeen.add(key);
						missing.push({
							key,
							tradeId: trade._id,
							tradeName: trade.tradeName,
							jobItem: row[1],
							serviceType,
							rows: [row],
						});
					} else {
						missing.find((m) => m.key === key).rows.push(row);
					}
				}
			}
		});

		setUploadedMappings(mappingsStructure);

		if (missing.length > 0) {
			setMissingJobs(missing);
			setSelectedMissingJobKeys(missing.map((m) => m.key));
			setMissingJobsModalOpen(true);
		}
	};

	const handleCreateSelectedMissingJobs = async () => {
		const selected = missingJobs.filter((m) => selectedMissingJobKeys.includes(m.key));
		if (selected.length === 0) {
			setMissingJobsModalOpen(false);
			return;
		}

		setCreatingMissingJobs(true);
		const updatedMappings = uploadedMappings ? [...uploadedMappings] : [];
		const stillMissing = [];
		const statusMap = { ...jobCreationStatus };
		selected.forEach((item) => {
			statusMap[item.key] = "pending";
		});
		setJobCreationStatus({ ...statusMap });

		let createdCount = 0;
		let failedCount = 0;

		for (const item of selected) {
			const result = await dispatch(
				createJob({
					values: {
						tradeId: item.tradeId,
						jobItem: item.jobItem,
						serviceType: item.serviceType,
					},
					silent: true,
				})
			);

			if (result?.type === "job/create/fulfilled" && result.payload?._id) {
				const createdJob = result.payload;
				item.rows.forEach((row) => {
					const jobMapping = buildJobMapping(
						row,
						createdJob._id,
						createdJob.jobItem,
						item.serviceType
					);
					insertMappingForTrade(updatedMappings, item.tradeId, item.serviceType, jobMapping);
				});
				statusMap[item.key] = "created";
				createdCount += 1;
			} else {
				stillMissing.push(item);
				statusMap[item.key] = "failed";
				failedCount += 1;
			}
			setJobCreationStatus({ ...statusMap });
		}

		setUploadedMappings(updatedMappings);
		setCreatingMissingJobs(false);

		if (createdCount > 0) {
			notification.success({
				message: "Jobs created",
				description:
					failedCount > 0
						? `${createdCount} job(s) created, ${failedCount} failed.`
						: `${createdCount} job item(s) successfully created.`,
				duration: 4,
			});
		} else if (failedCount > 0) {
			notification.error({
				message: "Job creation failed",
				description: `${failedCount} job(s) could not be created.`,
				duration: 4,
			});
		}

		setSelectedMissingJobKeys(stillMissing.map((m) => m.key));

		if (stillMissing.length === 0) {
			const skipped = missingJobs.filter((m) => !selectedMissingJobKeys.includes(m.key));
			const remaining = missingJobs.filter(
				(m) => statusMap[m.key] !== "created"
			);
			setMissingJobs(remaining);
			if (remaining.length === 0) {
				setMissingJobsModalOpen(false);
				if (skipped.length > 0) {
					notification.info({
						message: "Skipped jobs",
						description: `${skipped.length} job(s) were not created and their CSV rows were skipped.`,
						duration: 4,
					});
				}
			}
		}
	};

	const handleMappings = () => {
		const cleanedMappings = uploadedMappings.map(mapping => {
			const cleanMapping = {
				trade: mapping.trade,
				repair: [],
				maintenance: [],
				estimate: [],
				additionalServices: mapping.additionalServices || {},
				_id: mapping._id
			};

			const cleanJobEntry = (job) => ({
				jobItem: job.jobItem || '',
				jobItemName: job.jobItemName || '',
				serviceType: job.serviceType || '',
				is_active: Boolean(job.is_active),
				st_business_unit_id: job.st_business_unit_id || '',
				st_business_unit_name: job.st_business_unit_name || '',
				is_member: Boolean(job.is_member),
				st_member_job_id: job.st_member_job_id || '',
				st_member_job_name: job.st_member_job_name || '',
				st_non_member_job_id: job.st_non_member_job_id || '',
				st_non_member_job_name: job.st_non_member_job_name || '',
				dispatch_fee: Number(job.dispatch_fee) || 0,
				member_dispatch_fee: Number(job.member_dispatch_fee) || 0,
				use_default_dispatch: Boolean(job.use_default_dispatch),
				dispatch_header: job.dispatch_header || '',
				dispatch_body: job.dispatch_body || '',
				dispatch_text: job.dispatch_text || '',
				availability_mode: job.availability_mode === 'office_hours' ? 'office_hours' : '24/7',
				associated_questions: Array.isArray(job.associated_questions) ? job.associated_questions : []
			});

			['repair', 'maintenance', 'estimate'].forEach(serviceType => {
				if (Array.isArray(mapping[serviceType])) {
					cleanMapping[serviceType] = mapping[serviceType].map(cleanJobEntry);
				}
			});


			if (mapping.additionalServices) {
				cleanMapping.additionalServices = Object.keys(mapping.additionalServices).reduce((acc, key) => {
					if (Array.isArray(mapping.additionalServices[key])) {
						acc[key] = mapping.additionalServices[key].map(cleanJobEntry);
					}
					return acc;
				}, {});
			}

			return cleanMapping;
		});

		dispatch(
			updateOrganization({
				id: organizationData._id,
				payload: {
					jobs_mapping: cleanedMappings
				},
				page: "Upload Job Mappings"
			})
		);
	};

	return (
		<>
			<Flex align="center" gap={10} style={{ marginBottom: "50px" }}>
				<CloudServerOutlined style={{ fontSize: 20 }} />
				<Title level={3} style={{ margin: 0 }}>
					Upload Job Mappings
				</Title>
			</Flex>

			<CSVReader
				onUploadAccepted={(results) => {
					setUploadedMappings(null);
					handleMappingsUpload(results);
					setZoneHover(false);
				}}
				onDragOver={(event) => {
					event.preventDefault();
					setZoneHover(true);
				}}
				onDragLeave={(event) => {
					event.preventDefault();
					setZoneHover(false);
				}}
			>
				{({
					getRootProps,
					acceptedFile,
					ProgressBar,
					getRemoveFileProps,
					Remove,
				}) => (
					<>
						<div
							{...getRootProps()}
							style={Object.assign(
								{},
								styles.zone,
								zoneHover && styles.zoneHover
							)}
						>
							{acceptedFile ? (
								<>
									<div style={styles.file}>
										<div style={styles.info}>
											<span style={styles.size}>
												{formatFileSize(acceptedFile.size)}
											</span>
											<span style={styles.name}>{acceptedFile.name}</span>
										</div>
										<div style={styles.progressBar}>
											<ProgressBar />
										</div>
										<div
											{...getRemoveFileProps()}
											style={styles.remove}
											onMouseOver={(event) => {
												event.preventDefault();
												setRemoveHoverColor(REMOVE_HOVER_COLOR_LIGHT);
											}}
											onMouseOut={(event) => {
												event.preventDefault();
												setRemoveHoverColor(DEFAULT_REMOVE_HOVER_COLOR);
											}}
											onClick={(event) => {
												getRemoveFileProps().onClick(event);
												setUploadedMappings(null);
											}}
										>
											<Remove color={removeHoverColor} />
										</div>
									</div>
								</>
							) : (
								"Drop CSV file here or click to upload"
							)}
						</div>
					</>
				)}
			</CSVReader>

			<Button
				onClick={handleMappings}
				type="primary"
				style={{ marginTop: 50 }}
				disabled={uploadedMappings === null}
				loading={loading}
			>
				Save Mappings
			</Button>

			<Modal
				title="New Job Items Detected"
				open={missingJobsModalOpen}
				onCancel={() => {
					if (!creatingMissingJobs) setMissingJobsModalOpen(false);
				}}
				width={760}
				footer={[
					<Button
						key="skip"
						disabled={creatingMissingJobs}
						onClick={() => setMissingJobsModalOpen(false)}
					>
						Skip All
					</Button>,
					<Button
						key="create"
						type="primary"
						loading={creatingMissingJobs}
						disabled={selectedMissingJobKeys.length === 0}
						onClick={handleCreateSelectedMissingJobs}
					>
						Create Selected ({selectedMissingJobKeys.length})
					</Button>,
				]}
			>
				<Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
					The following job items from your CSV don't exist yet. Select which
					ones you want to create — unselected rows will be skipped.
				</Typography.Paragraph>
				<Table
					size="small"
					rowKey="key"
					dataSource={missingJobs}
					pagination={false}
					scroll={{ y: 360 }}
					rowSelection={{
						selectedRowKeys: selectedMissingJobKeys,
						onChange: (keys) => setSelectedMissingJobKeys(keys),
						getCheckboxProps: (record) => ({
							disabled:
								creatingMissingJobs || jobCreationStatus[record.key] === "created",
						}),
					}}
					columns={[
						{
							title: "",
							dataIndex: "key",
							width: 20,
							render: (key) => {
								const status = jobCreationStatus[key];
								if (status === "created")
									return <CheckCircleTwoTone twoToneColor="#52c41a" />;
								if (status === "failed")
									return <CloseCircleTwoTone twoToneColor="#ff4d4f" />;
								if (status === "pending") return <LoadingOutlined />;
								return null;
							},
						},
						{ title: "Trade", dataIndex: "tradeName" },
						{ title: "Job Item", dataIndex: "jobItem" },
						{
							title: "Service Type",
							dataIndex: "serviceType",
							render: (v) => <Tag>{v}</Tag>,
						},
					]}
				/>
			</Modal>
		</>
	);
};

export default JobsMappingUpload;
